/**
 * Constants for GuiBuilder
 * Shared configuration values and mappings
 */

// Input type to numeric ID mapping (for API calls)
export const INPUT_TYPE_MAP = {
    Dropdown: 0,
    Radio: 1,
    Checkbox: 2,
    MultiCheckbox: 3,
    Slider: 4,
    TextInput: 5,
    NumberInput: 6,
    TextArea: 7,
    DatePicker: 8,
    ColorPicker: 9,
    ImageDisplay: 10,
    ValueCards: 11,
    Swatches: 12,
    ImageCarousel: 13
};

// Input type metadata for display purposes
export const INPUT_TYPE_METADATA = {
    Dropdown: { label: 'Dropdown', icon: 'fa-solid fa-chevron-down', color: '#2563eb' },
    Radio: { label: 'Radio', icon: 'fa-solid fa-circle-dot', color: '#16a34a' },
    Checkbox: { label: 'Checkbox', icon: 'fa-solid fa-square-check', color: '#7c3aed' },
    MultiCheckbox: { label: 'Multi Checkbox', icon: 'fa-solid fa-check-square', color: '#db2777' },
    Slider: { label: 'Slider', icon: 'fa-solid fa-sliders', color: '#ea580c' },
    TextInput: { label: 'Text Input', icon: 'fa-solid fa-font', color: '#059669' },
    NumberInput: { label: 'Number', icon: 'fa-solid fa-hashtag', color: '#0891b2' },
    TextArea: { label: 'Text Area', icon: 'fa-solid fa-paragraph', color: '#64748b' },
    DatePicker: { label: 'Date', icon: 'fa-solid fa-calendar', color: '#84cc16' },
    ColorPicker: { label: 'Color', icon: 'fa-solid fa-palette', color: '#ec4899' },
    ImageDisplay: { label: 'Image', icon: 'fa-solid fa-image', color: '#f59e0b' },
    ValueCards: { label: 'Value cards', icon: 'fa-solid fa-table-cells-large', color: '#0f766e' },
    Swatches: { label: 'Swatches', icon: 'fa-solid fa-circle', color: '#be123c' },
    ImageCarousel: { label: 'Image carousel', icon: 'fa-solid fa-images', color: '#1d4ed8' }
};

// Friendly display names for layout blocks
export const FRIENDLY_BLOCK_NAMES = {
    'intro-panel': 'Intro / Welcome',
    'logo-display': 'Logo Display',
    'pnc-catalog': 'PNC Catalog',
    'pnc-search': 'PNC Search',
    'product-type-cards': 'Product Type Cards',
    'needs-configurator': 'Needs Configurator',
    'filter-panel': 'Filter',
    'compare-panel': 'Compare',
    'accordion-sections': 'Accordions',
    'tab-sections': 'Tabs',
    'process-line': 'Process Line',
    'all-in-one-panel': 'All-in-One Layout',
    'features-view': 'Features View (Form Content)',
    'image-carousel': 'Image Carousel',
    'selections-overview': 'Selections Overview Panel',
    'wizard-navigation': 'Wizard Navigation Buttons',
    'send-email': 'Send Email',
    'show-pnc': 'Show PNC',
    'exit-action': 'Exit Action Button'
};

// Layout template options
export const LAYOUT_TEMPLATES = [
    { value: 'split-70-30', label: '70/30 Columns (Main / Sidebar)' },
    { value: 'split-50-50', label: '50/50 Columns (Equal Columns)' },
    { value: 'split-30-70', label: '30/70 Columns (Sidebar / Main)' },
    { value: 'single-column', label: 'Single Column (Centered)' }
];

export const DEFAULT_LAYOUT_MAPPING = {
    column1: ['features-view', 'wizard-navigation'],
    column2: ['selections-overview']
};

export const DESIGN_MODULE_SECTIONS = [
    { id: 'start', label: 'Introduction and presentation' },
    { id: 'middle', label: 'Flow and interaction' },
    { id: 'end', label: 'Conclusion' }
];

export const FLOW_MODULE_IDS = ['accordions', 'tabs', 'processLine', 'allInOne'];

export const FLOW_BLOCK_IDS = ['accordion-sections', 'tab-sections', 'process-line', 'all-in-one-panel'];

export const FLOW_MODE_OPTIONS = [
    {
        id: 'accordions',
        label: 'Accordions',
        description: 'Open any section at any time.'
    },
    {
        id: 'tabs',
        label: 'Tabs',
        description: 'Switch between sections at any time.'
    },
    {
        id: 'processLine',
        label: 'Process',
        description: 'Move with next and previous. Required choices must be made before continuing.'
    },
    {
        id: 'allInOne',
        label: 'All in one',
        description: 'Everything on one page. Additional pages or accordion sections cannot be added.'
    }
];

export const DESIGN_MODULE_CATALOG = [
    {
        id: 'intro',
        section: 'start',
        label: 'Intro / welcome page',
        blockId: 'intro-panel',
        defaultConfig: { enabled: true, variant: 'welcome', title: 'Welcome', text: 'Start your configuration with a short introduction.', buttonText: 'Start' },
        variants: [
            { value: 'welcome', label: 'Welcome page' },
            { value: 'compact', label: 'Compact intro' },
            { value: 'hero', label: 'Hero intro' }
        ]
    },
    {
        id: 'logo',
        section: 'start',
        label: 'Logo display',
        blockId: 'logo-display',
        defaultConfig: {
            enabled: false,
            placement: 'top-left',
            text: 'Logo',
            logoId: '',
            logoName: '',
            logoUrl: '',
            logoOptions: []
        },
        variants: [
            { value: 'top-left', label: 'Top left' },
            { value: 'top-right', label: 'Top right' },
            { value: 'top', label: 'Top centered' },
            { value: 'bottom', label: 'Bottom centered' }
        ]
    },
    {
        id: 'pncCatalog',
        section: 'start',
        label: 'PNC Catalog',
        blockId: 'pnc-catalog',
        defaultConfig: { enabled: false, variant: 'catalog', title: 'PNC Catalog' }
    },
    {
        id: 'pncSearch',
        section: 'start',
        label: 'PNC search',
        blockId: 'pnc-search',
        defaultConfig: { enabled: false, variant: 'basic', placeholder: 'Search PNC...' },
        variants: [
            { value: 'basic', label: 'Basic search' },
            { value: 'with-help', label: 'With help text' }
        ]
    },
    {
        id: 'productTypeCards',
        section: 'start',
        label: 'Product type cards',
        blockId: 'product-type-cards',
        defaultConfig: { enabled: false, variant: 'cards-a', items: ['Washing machine', 'Dryer', 'Dishwasher'] },
        variants: [
            { value: 'cards-a', label: 'Cards A' },
            { value: 'cards-b', label: 'Cards B' },
            { value: 'cards-c', label: 'Cards C' }
        ]
    },
    {
        id: 'needsConfigurator',
        section: 'start',
        label: 'Needs configurator',
        blockId: 'needs-configurator',
        defaultConfig: { enabled: false, variant: 'guided-1', title: 'Find the right solution' },
        variants: [
            { value: 'guided-1', label: 'Guided 1' },
            { value: 'guided-2', label: 'Guided 2' },
            { value: 'guided-3', label: 'Guided 3' }
        ]
    },
    {
        id: 'filter',
        section: 'start',
        label: 'Filter',
        blockId: 'filter-panel',
        defaultConfig: { enabled: false }
    },
    {
        id: 'compare',
        section: 'start',
        label: 'Compare',
        blockId: 'compare-panel',
        defaultConfig: { enabled: false }
    },
    {
        id: 'accordions',
        section: 'middle',
        label: 'Accordions',
        blockId: 'accordion-sections',
        defaultConfig: { enabled: false, variant: 'standard', items: ['General', 'Dimensions', 'Installation'] },
        variants: [
            { value: 'standard', label: 'Standard' },
            { value: 'compact', label: 'Compact' }
        ]
    },
    {
        id: 'tabs',
        section: 'middle',
        label: 'Tabs',
        blockId: 'tab-sections',
        defaultConfig: { enabled: false, variant: 'top', items: ['Basics', 'Options', 'Summary'] },
        variants: [
            { value: 'top', label: 'Top tabs' },
            { value: 'side', label: 'Side tabs' }
        ]
    },
    {
        id: 'processLine',
        section: 'middle',
        label: 'Process',
        blockId: 'process-line',
        defaultConfig: { enabled: false, variant: 'process-line', steps: ['Choose', 'Configure', 'Review'] },
        variants: [
            { value: 'process-line', label: 'Process line' },
            { value: 'minimal', label: 'Minimal' }
        ]
    },
    {
        id: 'allInOne',
        section: 'middle',
        label: 'All in one',
        blockId: 'all-in-one-panel',
        defaultConfig: { enabled: false, variant: 'all-in-one' },
        variants: [
            { value: 'all-in-one', label: 'All in one' },
            { value: 'dense', label: 'Dense' }
        ]
    },
    {
        id: 'imageCarousel',
        section: 'middle',
        label: 'Image carousel',
        blockId: 'image-carousel',
        defaultConfig: { enabled: false, bindFeature: '' }
    },
    {
        id: 'sendEmail',
        section: 'end',
        label: 'Send email',
        blockId: 'send-email',
        defaultConfig: { enabled: false, buttonText: 'Send email', infoText: 'Send this configuration to an inbox.' }
    },
    {
        id: 'showPnc',
        section: 'end',
        label: 'Show PNC for configuration',
        blockId: 'show-pnc',
        defaultConfig: { enabled: false, label: 'PNC for configuration' }
    }
];

export const DEFAULT_DESIGN_SETTINGS = {
    version: 1,
    configured: false,
    styling: {
        source: 'hex',
        url: '',
        hexPrimary: '#2563eb',
        hexSecondary: '#14b8a6',
        cmykPrimary: { c: 80, m: 55, y: 0, k: 10 },
        cmykSecondary: { c: 72, m: 0, y: 28, k: 12 }
    },
    modules: DESIGN_MODULE_CATALOG.reduce((acc, module) => ({
        ...acc,
        [module.id]: module.defaultConfig
    }), {})
};

export const PNC_CATALOG_MODULE_IDS = ['pncSearch', 'productTypeCards', 'filter', 'compare'];

export { DEFAULT_EMBED_SETTINGS, DEFAULT_THEME } from '../../../utils/embedSnippet';
